import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useProjectStore } from '../../store/projectStore';
import { useUiStore } from '../../store/uiStore';

function generateShareCode(): string {
  const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  const array = new Uint8Array(8);
  crypto.getRandomValues(array);
  return Array.from(array, b => chars[b % chars.length]).join('');
}

const styles = {
  bar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: '56px',
    padding: '0 24px',
    backgroundColor: '#132840',
    borderBottom: '1px solid rgba(76,92,104,0.35)',
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    zIndex: 100,
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexShrink: 0,
  },
  backLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '12px',
    color: '#8FA4B5',
    cursor: 'pointer',
    border: 'none',
    background: 'none',
    padding: '4px 0',
    marginRight: '8px',
  },
  brandMark: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'flex-start',
    justifyContent: 'center',
    lineHeight: 1,
  },
  brandWordmark: {
    fontSize: '20px',
    fontWeight: 600,
    color: '#F5A621',
    fontFamily: "'Poppins', sans-serif",
    letterSpacing: '-0.5px',
    lineHeight: 1.1,
  },
  brandTagline: {
    fontSize: '9px',
    fontWeight: 400,
    color: '#4C5C68',
    fontFamily: "'DM Sans', sans-serif",
    letterSpacing: '0.3px',
    lineHeight: 1,
    textTransform: 'uppercase' as const,
  },
  brandName: {
    fontSize: '15px',
    fontWeight: 600,
    color: '#E8EDF2',
    letterSpacing: '-0.2px',
  },
  divider: {
    width: '1px',
    height: '24px',
    backgroundColor: 'rgba(76,92,104,0.35)',
    margin: '0 4px',
  },
  projectInfo: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '1px',
    overflow: 'hidden',
  },
  projectName: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#E8EDF2',
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  clientName: {
    fontSize: '11px',
    color: '#8FA4B5',
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  center: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flex: 1,
    justifyContent: 'center',
    padding: '0 24px',
  },
  right: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    flexShrink: 0,
  },
  scenarioLabel: {
    fontSize: '12px',
    color: '#8FA4B5',
    fontWeight: 500,
  },
  scenarioSelect: {
    fontSize: '13px',
    color: '#E8EDF2',
    backgroundColor: '#0a1929',
    border: '1px solid rgba(76,92,104,0.35)',
    borderRadius: '6px',
    padding: '4px 8px',
    cursor: 'pointer',
    outline: 'none',
    maxWidth: '200px',
  },
  saveStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
  },
  savingDot: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    animation: 'pulse 1s infinite',
  },
};

export function TopBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const project = useProjectStore(s => s.project);
  const clearProject = useProjectStore(s => s.clearProject);
  const setActiveScenario = useProjectStore(s => s.setActiveScenario);
  const addScenario = useProjectStore(s => s.addScenario);
  const updateScenario = useProjectStore(s => s.updateScenario);
  const deleteScenario = useProjectStore(s => s.deleteScenario);
  const setShareCode = useProjectStore(s => s.setShareCode);
  const isSaving = useUiStore(s => s.isSaving);
  const lastSaved = useUiStore(s => s.lastSaved);
  const saveError = useUiStore(s => s.saveError);
  const showToast = useUiStore(s => s.showToast);

  // Project delete
  const [shareOpen, setShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Scenario dropdown
  const [scenarioOpen, setScenarioOpen] = useState(false);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [newScenarioOpen, setNewScenarioOpen] = useState(false);
  const [newScenarioName, setNewScenarioName] = useState('');
  const [newScenarioClone, setNewScenarioClone] = useState(true);
  const scenarioDropdownRef = useRef<HTMLDivElement>(null);
  const renameInputRef = useRef<HTMLInputElement>(null);

  const activeScenarioId = project?.activeScenarioId ?? null;
  const activeScenario = project?.scenarios.find(s => s.id === activeScenarioId) ?? project?.scenarios[0];

  // Close scenario dropdown on outside click
  useEffect(() => {
    if (!scenarioOpen) return;
    function handleClick(e: MouseEvent) {
      if (scenarioDropdownRef.current && !scenarioDropdownRef.current.contains(e.target as Node)) {
        setScenarioOpen(false);
        setRenamingId(null);
        setDeleteConfirmId(null);
        setNewScenarioOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [scenarioOpen]);

  // Focus rename input when it appears
  useEffect(() => {
    if (renamingId && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [renamingId]);

  function startRename(id: string, currentName: string) {
    setDeleteConfirmId(null);
    setRenamingId(id);
    setRenameValue(currentName);
  }

  function commitRename() {
    if (!renamingId) return;
    const trimmed = renameValue.trim();
    if (trimmed) {
      updateScenario(renamingId, { name: trimmed });
    }
    setRenamingId(null);
  }

  function handleAddScenario() {
    const trimmed = newScenarioName.trim();
    if (!trimmed) return;
    const activeConfig = newScenarioClone ? activeScenario?.sizingConfig : undefined;
    addScenario(trimmed, activeConfig);
    setNewScenarioName('');
    setNewScenarioOpen(false);
    showToast(`Scenario "${trimmed}" created`);
  }

  function handleShare() {
    if (!project) return;
    if (!project.shareCode) {
      setShareCode(generateShareCode());
    }
    setShareOpen(v => !v);
    setCopied(false);
  }

  function handleCopy() {
    const code = project?.shareCode;
    if (!code) return;
    const url = `${window.location.origin}/share/${code}`;
    void navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  async function handleDeleteProject() {
    if (!project) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const res = await fetch(`/api/projects/${project.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setDeleteOpen(false);
      clearProject();
      showToast('Project deleted');
      navigate('/');
    } catch {
      setDeleteError('Failed to delete project. Please try again.');
    } finally {
      setDeleting(false);
    }
  }

  function formatLastSaved(ts: string | null): string {
    if (!ts) return '';
    const d = new Date(ts);
    return `Saved ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  }

  return (
    <header style={styles.bar} data-topbar>
      {/* Brand + Project */}
      <div style={styles.brand}>
        {location.pathname !== '/' && (
          <button
            type="button"
            style={styles.backLink}
            onClick={() => navigate('/')}
            aria-label="Back to projects"
          >
            ← Projects
          </button>
        )}
        <div style={styles.brandMark}>
          <span style={styles.brandWordmark}>een.</span>
          <span style={styles.brandTagline}>The Perfect Solution</span>
        </div>
        <span style={styles.brandName}>VM Sizing</span>
        {project && (
          <>
            <div style={styles.divider} />
            <div style={styles.projectInfo}>
              <span style={styles.projectName}>{project.name}</span>
              <span style={styles.clientName}>{project.clientName}</span>
            </div>
          </>
        )}
      </div>

      {/* Center — scenario switcher */}
      <div style={styles.center}>
        {project && (
          <div ref={scenarioDropdownRef} style={{ position: 'relative' }}>
            {/* Trigger button */}
            <button
              type="button"
              onClick={() => {
                setScenarioOpen(v => !v);
                if (scenarioOpen) {
                  setRenamingId(null);
                  setDeleteConfirmId(null);
                  setNewScenarioOpen(false);
                }
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '5px 10px',
                backgroundColor: scenarioOpen ? '#132840' : '#0d1f30',
                border: '1px solid rgba(76,92,104,0.35)',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px',
                color: '#E8EDF2',
                maxWidth: '260px',
              }}
              aria-haspopup="listbox"
              aria-expanded={scenarioOpen}
            >
              <span style={{ fontSize: '11px', color: '#8FA4B5', fontWeight: 500, flexShrink: 0 }}>
                Scenario:
              </span>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {activeScenario?.name ?? '—'}
              </span>
              {activeScenario?.isBaseline && (
                <span data-scenario-badge style={{
                  fontSize: '10px',
                  backgroundColor: 'rgba(206,236,248,0.12)',
                  color: '#CEECF8',
                  padding: '1px 5px',
                  borderRadius: '3px',
                  border: '1px solid rgba(206,236,248,0.2)',
                  flexShrink: 0,
                  fontWeight: 600,
                }}>
                  Baseline
                </span>
              )}
              <span style={{ marginLeft: '2px', color: '#8FA4B5', fontSize: '10px', flexShrink: 0 }}>
                {scenarioOpen ? '▲' : '▼'}
              </span>
            </button>

            {/* Dropdown panel */}
            {scenarioOpen && (
              <div
                role="listbox"
                aria-label="Scenarios"
                style={{
                  position: 'absolute',
                  top: '38px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 300,
                  backgroundColor: '#0d1f30',
                  border: '1px solid rgba(76,92,104,0.35)',
                  borderRadius: '8px',
                  padding: '6px',
                  minWidth: '280px',
                  maxWidth: '360px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
                }}
              >
                {project.scenarios.map(sc => {
                  const isActive = sc.id === activeScenarioId;
                  const isRenaming = renamingId === sc.id;
                  const isDeleteConfirm = deleteConfirmId === sc.id;
                  const canDelete = !sc.isBaseline && project.scenarios.length > 1;

                  return (
                    <div
                      key={sc.id}
                      role="option"
                      aria-selected={isActive}
                      style={{
                        borderRadius: '5px',
                        marginBottom: '2px',
                        backgroundColor: isActive ? '#132840' : 'transparent',
                        overflow: 'hidden',
                      }}
                    >
                      {isDeleteConfirm ? (
                        /* Inline delete confirmation */
                        <div style={{ padding: '8px 10px' }}>
                          <p style={{ margin: '0 0 8px', fontSize: '12px', color: '#fca5a5' }}>
                            Delete <strong>{sc.name}</strong>? This cannot be undone.
                          </p>
                          <div style={{ display: 'flex', gap: '6px' }}>
                            <button
                              type="button"
                              onClick={() => setDeleteConfirmId(null)}
                              style={{
                                flex: 1,
                                padding: '4px 0',
                                backgroundColor: 'transparent',
                                color: '#8FA4B5',
                                border: '1px solid rgba(76,92,104,0.35)',
                                borderRadius: '4px',
                                fontSize: '11px',
                                cursor: 'pointer',
                              }}
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                deleteScenario(sc.id);
                                setDeleteConfirmId(null);
                                showToast(`Scenario "${sc.name}" deleted`);
                              }}
                              style={{
                                flex: 1,
                                padding: '4px 0',
                                backgroundColor: 'rgba(220,60,60,0.15)',
                                color: '#E05555',
                                border: '1px solid rgba(220,60,60,0.3)',
                                borderRadius: '4px',
                                fontSize: '11px',
                                fontWeight: 600,
                                cursor: 'pointer',
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      ) : isRenaming ? (
                        /* Inline rename */
                        <div style={{ padding: '6px 8px', display: 'flex', gap: '5px' }}>
                          <input
                            ref={renameInputRef}
                            value={renameValue}
                            onChange={e => setRenameValue(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') commitRename();
                              if (e.key === 'Escape') setRenamingId(null);
                            }}
                            onBlur={commitRename}
                            style={{
                              flex: 1,
                              padding: '4px 7px',
                              backgroundColor: '#0a1929',
                              border: '1px solid rgba(245,166,33,0.5)',
                              borderRadius: '4px',
                              fontSize: '12px',
                              color: '#E8EDF2',
                              outline: 'none',
                            }}
                          />
                          <button
                            type="button"
                            onMouseDown={e => { e.preventDefault(); commitRename(); }}
                            style={{
                              padding: '4px 8px',
                              backgroundColor: 'rgba(245,166,33,0.12)',
                              color: '#F5A621',
                              border: '1px solid rgba(245,166,33,0.5)',
                              borderRadius: '4px',
                              fontSize: '11px',
                              fontWeight: 600,
                              cursor: 'pointer',
                            }}
                          >
                            Save
                          </button>
                        </div>
                      ) : (
                        /* Normal scenario row */
                        <div style={{ display: 'flex', alignItems: 'center', padding: '6px 8px', gap: '6px' }}>
                          <button
                            type="button"
                            onClick={() => {
                              setActiveScenario(sc.id);
                              setScenarioOpen(false);
                            }}
                            style={{
                              flex: 1,
                              background: 'none',
                              border: 'none',
                              textAlign: 'left',
                              cursor: 'pointer',
                              padding: 0,
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              minWidth: 0,
                            }}
                          >
                            <span style={{
                              width: '6px',
                              height: '6px',
                              borderRadius: '50%',
                              flexShrink: 0,
                              backgroundColor: isActive ? '#CEECF8' : 'transparent',
                              border: isActive ? 'none' : '1px solid rgba(76,92,104,0.35)',
                            }} />
                            <span style={{
                              fontSize: '13px',
                              color: isActive ? '#E8EDF2' : '#8FA4B5',
                              fontWeight: isActive ? 600 : 400,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}>
                              {sc.name}
                            </span>
                            {sc.isBaseline && (
                              <span style={{
                                fontSize: '10px',
                                backgroundColor: 'rgba(206,236,248,0.12)',
                                color: '#CEECF8',
                                padding: '1px 5px',
                                borderRadius: '3px',
                                border: '1px solid rgba(206,236,248,0.2)',
                                flexShrink: 0,
                                fontWeight: 600,
                              }}>
                                Baseline
                              </span>
                            )}
                          </button>

                          {/* Rename button */}
                          <button
                            type="button"
                            onClick={() => startRename(sc.id, sc.name)}
                            title="Rename scenario"
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              color: '#8FA4B5',
                              padding: '2px 4px',
                              borderRadius: '3px',
                              fontSize: '11px',
                              flexShrink: 0,
                            }}
                          >
                            ✎
                          </button>

                          {/* Delete button */}
                          <button
                            type="button"
                            onClick={() => canDelete && setDeleteConfirmId(sc.id)}
                            disabled={!canDelete}
                            title={!canDelete ? (sc.isBaseline ? 'Cannot delete baseline scenario' : 'Cannot delete the only scenario') : 'Delete scenario'}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: canDelete ? 'pointer' : 'not-allowed',
                              color: canDelete ? '#E05555' : '#4C5C68',
                              padding: '2px 4px',
                              borderRadius: '3px',
                              fontSize: '11px',
                              flexShrink: 0,
                              opacity: canDelete ? 1 : 0.4,
                            }}
                          >
                            ✕
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Divider */}
                <div style={{ height: '1px', backgroundColor: 'rgba(76,92,104,0.35)', margin: '4px 0' }} />

                {/* Add new scenario */}
                {newScenarioOpen ? (
                  <div style={{ padding: '8px' }}>
                    <input
                      autoFocus
                      placeholder="Scenario name…"
                      value={newScenarioName}
                      onChange={e => setNewScenarioName(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') handleAddScenario();
                        if (e.key === 'Escape') setNewScenarioOpen(false);
                      }}
                      style={{
                        width: '100%',
                        padding: '6px 8px',
                        backgroundColor: '#0a1929',
                        border: '1px solid rgba(76,92,104,0.35)',
                        borderRadius: '5px',
                        fontSize: '12px',
                        color: '#E8EDF2',
                        outline: 'none',
                        boxSizing: 'border-box',
                        marginBottom: '6px',
                      }}
                    />
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '11px',
                      color: '#8FA4B5',
                      cursor: 'pointer',
                      marginBottom: '8px',
                    }}>
                      <input
                        type="checkbox"
                        checked={newScenarioClone}
                        onChange={e => setNewScenarioClone(e.target.checked)}
                      />
                      Clone config from active scenario
                    </label>
                    <div style={{ display: 'flex', gap: '5px' }}>
                      <button
                        type="button"
                        onClick={() => setNewScenarioOpen(false)}
                        style={{
                          flex: 1,
                          padding: '5px 0',
                          backgroundColor: 'transparent',
                          color: '#8FA4B5',
                          border: '1px solid rgba(76,92,104,0.35)',
                          borderRadius: '4px',
                          fontSize: '12px',
                          cursor: 'pointer',
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleAddScenario}
                        disabled={!newScenarioName.trim()}
                        style={{
                          flex: 1,
                          padding: '5px 0',
                          backgroundColor: newScenarioName.trim() ? '#F5A621' : '#0d1f30',
                          color: newScenarioName.trim() ? '#021526' : '#4C5C68',
                          border: 'none',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: 600,
                          cursor: newScenarioName.trim() ? 'pointer' : 'not-allowed',
                        }}
                      >
                        Create
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setNewScenarioOpen(true);
                      setNewScenarioName('');
                      setNewScenarioClone(true);
                    }}
                    style={{
                      width: '100%',
                      padding: '7px 10px',
                      backgroundColor: 'transparent',
                      border: 'none',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      color: '#8FA4B5',
                      textAlign: 'left',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    <span style={{ fontSize: '14px', color: '#CEECF8' }}>+</span>
                    New scenario…
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Right — save status + share */}
      <div style={styles.right}>
        {isSaving && (
          <div style={{ ...styles.saveStatus, color: '#8FA4B5' }}>
            <div style={{ ...styles.savingDot, backgroundColor: '#F5A621', animation: 'pulse 1s infinite' }} />
            Saving…
          </div>
        )}
        {!isSaving && saveError && (
          <div style={{ ...styles.saveStatus, color: '#E05555' }} title={saveError}>
            <div style={{ ...styles.savingDot, backgroundColor: '#E05555' }} />
            Save failed
          </div>
        )}
        {!isSaving && !saveError && lastSaved && (
          <div style={{ ...styles.saveStatus, color: '#8FA4B5' }}>
            <div style={{ ...styles.savingDot, backgroundColor: '#22c55e' }} />
            {formatLastSaved(lastSaved)}
          </div>
        )}

        {project && (
          <>
          <button
            type="button"
            onClick={() => { setDeleteOpen(true); setDeleteError(null); }}
            style={{
              padding: '5px 12px',
              backgroundColor: 'rgba(220,60,60,0.15)',
              color: '#E05555',
              border: '1px solid rgba(220,60,60,0.3)',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: 500,
              cursor: 'pointer',
            }}
            aria-label="Delete project"
          >
            Delete
          </button>
          <div style={{ position: 'relative' }}>
            <button
              type="button"
              onClick={handleShare}
              style={{
                padding: '5px 12px',
                backgroundColor: shareOpen ? '#132840' : 'transparent',
                color: '#8FA4B5',
                border: '1px solid rgba(76,92,104,0.35)',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: 500,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
              }}
              aria-label="Share project"
            >
              ↗ Share
            </button>

            {shareOpen && project.shareCode && (
              <div style={{
                position: 'absolute',
                top: '36px',
                right: 0,
                zIndex: 200,
                backgroundColor: '#0d1f30',
                border: '1px solid rgba(76,92,104,0.35)',
                borderRadius: '8px',
                padding: '14px 16px',
                width: '320px',
                boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
              }}>
                <p style={{ margin: '0 0 8px', fontSize: '12px', color: '#8FA4B5', fontWeight: 500 }}>
                  Share link (read-only)
                </p>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <input
                    readOnly
                    value={`${window.location.origin}/share/${project.shareCode}`}
                    style={{
                      flex: 1,
                      backgroundColor: '#0a1929',
                      border: '1px solid rgba(76,92,104,0.35)',
                      borderRadius: '5px',
                      padding: '5px 8px',
                      fontSize: '11px',
                      color: '#8FA4B5',
                      outline: 'none',
                      fontFamily: 'monospace',
                    }}
                    onFocus={e => e.target.select()}
                  />
                  <button
                    type="button"
                    onClick={handleCopy}
                    style={{
                      padding: '5px 10px',
                      backgroundColor: copied ? '#166534' : '#F5A621',
                      color: copied ? '#fff' : '#021526',
                      border: 'none',
                      borderRadius: '5px',
                      fontSize: '11px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {copied ? '✓ Copied' : 'Copy'}
                  </button>
                </div>
                <p style={{ margin: '8px 0 0', fontSize: '11px', color: '#4C5C68' }}>
                  Anyone with this link can view the sizing results (no login required).
                </p>
              </div>
            )}
          </div>
          </>
        )}
      </div>

      {/* Delete confirmation dialog */}
      {deleteOpen && project && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="topbar-confirm-delete-title"
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.6)',
            zIndex: 400,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div style={{
            backgroundColor: '#0d1f30',
            border: '1px solid rgba(76,92,104,0.35)',
            borderRadius: '12px',
            padding: '28px 32px',
            maxWidth: '400px',
            width: '100%',
            boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
          }}>
            <p id="topbar-confirm-delete-title" style={{ fontSize: '16px', fontWeight: 700, color: '#E8EDF2', margin: '0 0 8px' }}>
              Delete project?
            </p>
            <p style={{ fontSize: '13px', color: '#8FA4B5', lineHeight: 1.6, margin: '0 0 24px' }}>
              <strong style={{ color: '#E8EDF2' }}>{project.name}</strong> will be permanently deleted
              along with all its applications, VMs, and scenarios. This cannot be undone.
            </p>
            {deleteError && (
              <p style={{ fontSize: '12px', color: '#fca5a5', margin: '0 0 16px' }}>{deleteError}</p>
            )}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setDeleteOpen(false)}
                disabled={deleting}
                style={{
                  padding: '7px 16px',
                  backgroundColor: 'transparent',
                  color: '#8FA4B5',
                  border: '1px solid rgba(76,92,104,0.35)',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleDeleteProject()}
                disabled={deleting}
                autoFocus
                style={{
                  padding: '7px 16px',
                  backgroundColor: 'rgba(220,60,60,0.15)',
                  color: '#E05555',
                  border: '1px solid rgba(220,60,60,0.3)',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  opacity: deleting ? 0.7 : 1,
                }}
              >
                {deleting ? 'Deleting…' : 'Delete project'}
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
